import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { APPOINTMENT_PREVIEW_FIELD_LABELS } from '@pages/Calendar/data';
import type { AppointmentPreviewField } from '@pages/Calendar/data';
import { AppointmentCardContent } from '@pages/Calendar/ui/AppointmentCard';

import { APPOINTMENT_PREVIEW_MOCK } from '../data/constants';
import { useAppointmentPreviewSettings } from '../hooks';

import { AppointmentPreviewFieldRow } from './AppointmentPreviewFieldRow';

import type { FC } from 'react';

import './AppointmentPreviewSettings.scss';

interface AppointmentPreviewSettingsProps {
  fields: AppointmentPreviewField[];
  onFieldsChange(fields: AppointmentPreviewField[]): void;
}

// Journal setting: which rows the appointment preview shows and in what order,
// with a mock appointment that renders the same rows as the grid card.
export const AppointmentPreviewSettings: FC<
  AppointmentPreviewSettingsProps
> = ({ fields, onFieldsChange }) => {
  const {
    sensors,
    sortableIds,
    visibleFieldsCount,
    dragEndHandler,
    moveFieldHandler,
    toggleFieldHandler,
  } = useAppointmentPreviewSettings({ fields, onFieldsChange });

  return (
    <div
      className="AppointmentPreviewSettings"
      data-testid="appointment-preview-settings"
    >
      <div className="AppointmentPreviewSettings__preview" aria-hidden>
        <div className="AppointmentPreviewSettings__card">
          <div className="AppointmentCard__header">
            <span className="AppointmentCard__time-range">
              {APPOINTMENT_PREVIEW_MOCK.timeRange}
            </span>
          </div>
          <div className="AppointmentCard__body">
            <AppointmentCardContent
              previewFields={fields}
              services={APPOINTMENT_PREVIEW_MOCK.services}
              hiddenServicesCount={0}
              primaryText={APPOINTMENT_PREVIEW_MOCK.services[0]}
              phone={APPOINTMENT_PREVIEW_MOCK.phone}
              clientName={APPOINTMENT_PREVIEW_MOCK.clientName}
              tags={APPOINTMENT_PREVIEW_MOCK.tags}
              hiddenTagsCount={0}
              comment={APPOINTMENT_PREVIEW_MOCK.comment}
            />
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={dragEndHandler}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          <ul className="AppointmentPreviewSettings__fields">
            {fields.map((field, index) => (
              <AppointmentPreviewFieldRow
                key={field.key}
                field={field}
                label={APPOINTMENT_PREVIEW_FIELD_LABELS[field.key]}
                isFirst={index === 0}
                isLast={index === fields.length - 1}
                isToggleDisabled={field.isVisible && visibleFieldsCount === 1}
                onMove={moveFieldHandler}
                onToggle={toggleFieldHandler}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};
